import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export interface ServicePricingPackage {
  id: string;
  service_id: string;
  package_name: string;
  package_name_en?: string;
  package_name_ru?: string;
  price: number;
  currency: string;
  description?: string;
  description_en?: string;
  description_ru?: string;
  features?: string[];
  is_popular: boolean;
  is_active: boolean;
  order_index: number;
  created_at?: string;
  updated_at?: string;
}

// Fetch all pricing packages for a service
export const useServicePricingPackages = (serviceId: string) => {
  return useQuery({
    queryKey: ["service-pricing-packages", serviceId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("service_pricing_packages")
        .select("*")
        .eq("service_id", serviceId)
        .order("order_index", { ascending: true });

      if (error) throw error;
      return data as ServicePricingPackage[];
    },
    enabled: !!serviceId,
  });
};

// Fetch all pricing packages (admin)
export const useAllServicePricingPackages = () => {
  return useQuery({
    queryKey: ["all-service-pricing-packages"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("service_pricing_packages")
        .select(`
          *,
          services:service_id (
            title,
            slug
          )
        `)
        .order("order_index", { ascending: true });

      if (error) throw error;
      return data;
    },
  });
};

// Create pricing package
export const useCreateServicePricingPackage = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (packageData: Omit<ServicePricingPackage, "id" | "created_at" | "updated_at">) => {
      const { data, error } = await supabase
        .from("service_pricing_packages")
        .insert(packageData)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["service-pricing-packages"] });
      queryClient.invalidateQueries({ queryKey: ["all-service-pricing-packages"] });
      toast.success("Գնային փաթեթը հաջողությամբ ավելացվել է");
    },
    onError: (error) => {
      toast.error("Սխալ է տեղի ունեցել: " + error.message);
    },
  });
};

// Update pricing package
export const useUpdateServicePricingPackage = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<ServicePricingPackage> & { id: string }) => {
      const { data, error } = await supabase
        .from("service_pricing_packages")
        .update(updates)
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["service-pricing-packages"] });
      queryClient.invalidateQueries({ queryKey: ["all-service-pricing-packages"] });
      toast.success("Գնային փաթեթը հաջողությամբ թարմացվել է");
    },
    onError: (error) => {
      toast.error("Սխալ է տեղի ունեցել: " + error.message);
    },
  });
};

// Delete pricing package
export const useDeleteServicePricingPackage = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("service_pricing_packages")
        .delete()
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["service-pricing-packages"] });
      queryClient.invalidateQueries({ queryKey: ["all-service-pricing-packages"] });
      toast.success("Գնային փաթեթը հաջողությամբ ջնջվել է");
    },
    onError: (error) => {
      toast.error("Սխալ է տեղի ունեցել: " + error.message);
    },
  });
};
